import { AssigneeName } from '@Purchasing/ui/AssigneeName'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { FilterSelect } from '@/components/filter-select'
import { Button } from '@/components/ui/button'
import { StageManager } from './StageManager'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '@Core/Hooks/usePermissions'
import { PurchasingAPI } from '@Purchasing/Data/DataSource/API/PurchasingAPI'
import { PurchasingBoard as BoardData, PurchasingBoardCard, PurchasingStage } from '@Purchasing/Domain/Model/PurchaseProcess'
import { processStatusLabelSafe, purchaseStatusLabelSafe } from '@Purchasing/presentation/purchasing-labels'
import { roles } from '@Purchasing/roles'
import { RoutesPaths } from '@Purchasing/RoutesPaths'
import { formatRouteId } from '@Utils/Formatters/formatRouteId'

const financeLabels: Record<PurchasingBoardCard['financeStatus'], string> = {
    NONE: 'Sem pagamentos', PENDING: 'A pagar', PARTIAL: 'Parcialmente pago',
    PAID: 'Pago', OVERDUE: 'Pagamento vencido',
}
const dragType = 'PURCHASING_PROCESS'
const businessDay = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date)
    const value = (name: string) => Number(parts.find((part) => part.type === name)?.value)
    return Date.UTC(value('year'), value('month') - 1, value('day'))
}
export const daysInPurchasingStage = (enteredAt: string, now = new Date()) =>
    Math.max(0, Math.round((businessDay(now) - businessDay(new Date(enteredAt))) / 86400000))
const stageDateTime = (value: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))

const BoardCard = ({ card, stages, canMove, moving, onMove }: {
    card: PurchasingBoardCard
    stages: PurchasingStage[]
    canMove: boolean
    moving: boolean
    onMove: (id: string, stageId: string) => Promise<void>
}) => {
    const navigate = useNavigate()
    const [{ dragging }, drag] = useDrag(() => ({
        type: dragType, item: { id: card._id, stageId: card.stageId },
        canDrag: canMove && !moving,
        collect: (monitor) => ({ dragging: monitor.isDragging() }),
    }), [card._id, card.stageId, canMove, moving])
    const days = daysInPurchasingStage(card.stageEnteredAt)
    return (
        <article ref={drag} className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm ${dragging ? 'opacity-40' : ''}`}>
            <button type="button" className="text-left w-full" onClick={() => navigate(formatRouteId(RoutesPaths.PurchasingDetail, card._id))}>
                <span className="text-xs font-semibold text-brand-deep">{card.code}</span>
                <strong className="block text-sm mt-1">{card.title}</strong>
            </button>
            <p className="text-xs text-gray-600 mt-2">{card.customer || card.company || 'Cliente não informado'}{card.quantity ? ` · ${card.quantity} un.` : ''}</p>
            <div className="flex flex-wrap gap-1 mt-2 text-xs">
                <span className="rounded bg-gray-100 px-2 py-1">{processStatusLabelSafe(card.status)}</span>
                <span className="rounded bg-gray-100 px-2 py-1">{card.selectedSupplierQuoteId ? 'Cotação selecionada' : 'Sem cotação vencedora'}</span>
                {card.purchase?._id && <span className="rounded bg-gray-100 px-2 py-1">{purchaseStatusLabelSafe(card.purchase.status || '')}</span>}
                {card.productionCount > 0 && <span className="rounded bg-gray-100 px-2 py-1">Produção vinculada</span>}
                <span className="rounded bg-gray-100 px-2 py-1">{financeLabels[card.financeStatus] || 'Financeiro não informado'}</span>
            </div>
            <div className="mt-3 text-xs text-gray-500 flex justify-between gap-2">
                <span className="min-w-0" data-testid={`card-assignee-${card._id}`}>
                    <AssigneeName value={card.assignedTo} size="sm" />
                </span>
                <span title={stageDateTime(card.stageEnteredAt)}>há {days} {days === 1 ? 'dia' : 'dias'}</span>
            </div>
            {canMove && <FilterSelect
                label={`Mover ${card.code} para estágio`}
                placeholder="Mover para…"
                value=""
                disabled={moving}
                onChange={(stageId) => void onMove(card._id, stageId)}
                options={stages.filter((stage) => stage.active !== false && stage._id !== card.stageId).map((stage) => ({ value: stage._id, label: stage.name }))}
                className="mt-3 h-8 w-full text-xs"
            />}
        </article>
    )
}

const BoardColumn = ({ stage, count, cards, stages, canMove, moving, onMove }: {
    stage: PurchasingStage
    count: number
    cards: PurchasingBoardCard[]
    stages: PurchasingStage[]
    canMove: boolean
    moving: boolean
    onMove: (id: string, stageId: string) => Promise<void>
}) => {
    const [{ over }, drop] = useDrop(() => ({
        accept: dragType,
        canDrop: (item: { id: string; stageId?: string | null }) => canMove && stage.active !== false && item.stageId !== stage._id && !moving,
        drop: (item: { id: string; stageId?: string | null }) => { void onMove(item.id, stage._id) },
        collect: (monitor) => ({ over: monitor.isOver() && monitor.canDrop() }),
    }), [canMove, stage._id, stage.active, moving, onMove])
    return <section ref={drop} className={`min-w-[275px] w-[300px] max-md:w-full shrink-0 rounded-xl border p-3 ${over ? 'border-primary bg-accent' : 'border-gray-200 bg-gray-50'}`} aria-label={`Estágio ${stage.name}`}>
        <header className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">{stage.name}{stage.active === false ? ' (inativo)' : ''}</h2>
            <span className="rounded-full bg-white border px-2 text-xs" aria-label={`${count} processos`}>{count}</span>
        </header>
        <div className="space-y-3">{cards.length ? cards.map((card) => <BoardCard key={card._id} card={card} stages={stages} canMove={canMove} moving={moving} onMove={onMove} />) : <p className="text-sm text-gray-500">{count ? 'Nenhum processo nesta página' : 'Nenhum processo nesta etapa'}</p>}</div>
    </section>
}

export const PurchasingBoard = ({ search, status, assignedTo = '' }: { search: string; status?: string; assignedTo?: string }) => {
    const api = useMemo(() => new PurchasingAPI(), [])
    const { hasPermission } = usePermissions()
    const canMove = hasPermission(roles.process.permissions.stage.key)
    const canManage = hasPermission(roles.stage.permissions.manage.key)
    const canReadStages = canManage || hasPermission(roles.stage.permissions.read.key)
    const [board, setBoard] = useState<BoardData | null>(null)
    const [page, setPage] = useState(1)
    const [winner, setWinner] = useState('')
    const [purchase, setPurchase] = useState('')
    const [production, setProduction] = useState('')
    const [financeStatus, setFinanceStatus] = useState('')
    const [stageId, setStageId] = useState('')
    const [loading, setLoading] = useState(false)
    const [moving, setMoving] = useState(false)
    const [error, setError] = useState('')
    const [stageBusy, setStageBusy] = useState(false)
    useEffect(() => { setPage(1) }, [search, status, assignedTo])
    // Filtros trocados em sequência disparam buscas paralelas: só a última vale.
    const requestRef = useRef(0)
    const load = useCallback(async () => {
        const request = ++requestRef.current
        setLoading(true)
        try {
            const next = await api.getBoard({ search, status, winner, purchase, production, financeStatus, stageId, assignedTo, page })
            if (request !== requestRef.current) return
            setBoard(next)
            setError('')
        } catch (cause: any) {
            if (request !== requestRef.current) return
            console.error(cause)
            setError(cause?.response?.data?.message || 'Não foi possível carregar o Kanban')
        } finally { if (request === requestRef.current) setLoading(false) }
    }, [api, search, status, winner, purchase, production, financeStatus, stageId, assignedTo, page])
    useEffect(() => { void load() }, [load])
    const move = useCallback(async (id: string, destination: string) => {
        if (!destination || moving) return
        setMoving(true)
        try {
            await api.updateProcessStage(id, { stageId: destination })
            await load()
        } catch (cause: any) {
            setError(cause?.response?.data?.message || 'Não foi possível mover o processo')
        } finally { setMoving(false) }
    }, [api, load, moving])
    const mutateStage = async (action: () => Promise<unknown>) => {
        setStageBusy(true)
        try { await action(); await load() } catch (cause: any) { setError(cause?.response?.data?.message || 'Não foi possível alterar o estágio') }
        finally { setStageBusy(false) }
    }
    const withPage = (setter: (value: string) => void) => (value: string) => { setter(value); setPage(1) }
    const yesNo = (yes: string, no: string) => [{ value: 'yes', label: yes }, { value: 'no', label: no }]
    const counts = new Map(board?.counts.map((count) => [count.stageId, count.count]) || [])
    const stages = board?.stages || []
    const visibleStages = (counts.get('') || 0) > 0
        ? [...stages, { _id: '', key: 'unassigned', name: 'Sem estágio', position: Number.MAX_SAFE_INTEGER, active: false }]
        : stages
    return <div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
            <FilterSelect label="Filtrar estágio" allLabel="Todos os estágios" value={stageId} onChange={withPage(setStageId)}
                options={stages.map((stage) => ({ value: stage._id, label: stage.name }))} />
            <FilterSelect label="Filtrar cotação" allLabel="Todas as cotações" value={winner} onChange={withPage(setWinner)}
                options={yesNo('Com vencedora', 'Sem vencedora')} />
            <FilterSelect label="Filtrar compra" allLabel="Todas as compras" value={purchase} onChange={withPage(setPurchase)}
                options={yesNo('Com compra', 'Sem compra')} />
            <FilterSelect label="Filtrar produção" allLabel="Toda a produção" value={production} onChange={withPage(setProduction)}
                options={yesNo('Com vínculo', 'Sem vínculo')} />
            <FilterSelect label="Filtrar financeiro" allLabel="Todo o financeiro" value={financeStatus} onChange={withPage(setFinanceStatus)}
                options={Object.entries(financeLabels).map(([value, label]) => ({ value, label }))} />
            {canReadStages && <div className="ml-auto">
                <StageManager
                    stages={stages}
                    counts={counts}
                    canManage={canManage}
                    busy={stageBusy}
                    onMove={(stage, direction) => mutateStage(() => api.moveStage(stage._id, direction))}
                    onRename={(stage, name) => mutateStage(() => api.updateStage(stage._id, { name }))}
                    onToggleActive={(stage) => mutateStage(() => api.updateStage(stage._id, { active: stage.active === false }))}
                    onCreate={(name) => mutateStage(() => api.createStage(name))}
                />
            </div>}
        </div>
        {error && <p role="alert" className="text-od-secondary-600 mb-3">{error}</p>}
        {loading && <p>Carregando Kanban…</p>}
        <DndProvider backend={HTML5Backend}>
            <div className="flex gap-4 overflow-x-auto pb-4 md:min-h-[360px] max-md:flex-col" aria-label="Quadro Kanban de compras">
                {visibleStages.filter((stage) => stage.active !== false || (counts.get(stage._id) || 0) > 0).map((stage) => <BoardColumn key={stage._id || 'unassigned'} stage={stage} count={counts.get(stage._id) || 0} cards={(board?.cards || []).filter((card) => String(card.stageId ?? '') === stage._id)} stages={stages} canMove={canMove} moving={moving} onMove={move} />)}
            </div>
        </DndProvider>
        <div className="flex flex-wrap items-center gap-2">
            {page > 1 && <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" strokeWidth={1.5} />Página anterior</Button>}
            {board && board.counts.some((count) => count.count > page * board.limit) && <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setPage(page + 1)}>Próxima página<ChevronRight className="h-4 w-4" strokeWidth={1.5} /></Button>}
            {board && <p className="text-xs text-muted-foreground">Exibindo até {board.limit} processos por estágio nesta página, de {board.total} filtrados.</p>}
        </div>
    </div>
}
